import React, { useState, useEffect, useRef } from 'react';
import styles from './Careers.module.css';
import joinTeamIcon from "../assets/we're_hiring.svg";
import CustomFileInput from '../components/CustomFileInput';
import { apiClient } from '../../services';
import { digitalOceanSpacesService } from '../../services/digitalOceanSpacesService';
import { CreateJobApplicationRequestDTO } from '@onaptek/lbtiling.restapi-access-sdk';
import SuccessModal from '../components/SuccessModal';
import Button from '../components/Button';

const Careers: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedData, setSubmittedData] = useState<CreateJobApplicationRequestDTO | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [visibleElements, setVisibleElements] = useState<number[]>([]);
  const sectionRef = useRef<HTMLElement>(null);

  const [formData, setFormData] = useState<CreateJobApplicationRequestDTO>({
    name: '',
    email: '',
    phone: '',
    positionAppliedFor: ''
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Staggered animation for icon, title, intro, instruction, form fields (5), button
            const totalElements = 10;
            for (let i = 0; i < totalElements; i++) {
              setTimeout(() => {
                setVisibleElements(prev => {
                  if (!prev.includes(i)) {
                    return [...prev, i];
                  }
                  return prev;
                });
              }, i * 150); // 150ms delay between each element
            }
          }
        });
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear any previous messages when user starts typing
    if (submitError) setSubmitError(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file using the Digital Ocean Spaces service
      const validation = digitalOceanSpacesService.validateResumeFile(file);
      if (!validation.isValid) {
        setSubmitError(validation.errors.join(', '));
        setSelectedFile(null);
        return;
      }
      
      setSelectedFile(file);
      console.log('File selected:', file);
      // Clear any previous error messages
      if (submitError) setSubmitError(null);
    } else {
      setSelectedFile(null);
    }
  };

  const validateJobApplicationData = (data: CreateJobApplicationRequestDTO): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Name validation
    if (!data.name || data.name.trim() === '') {
      errors.push('Full name is required');
    }

    // Email validation
    if (!data.email || data.email.trim() === '') {
      errors.push('Email address is required');
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(data.email)) {
        errors.push('Invalid email format');
      }
    }

    // Position validation
    if (!data.positionAppliedFor || data.positionAppliedFor.trim() === '') {
      errors.push('Position applied for is required');
    }

    // Phone validation
    if (!data.phone || data.phone.trim() === '') {
      errors.push('Phone number is required');
    } else { 
      const phoneDigits = data.phone.replace(/\D/g, '');
      if (phoneDigits.length < 10 || phoneDigits.length > 11) {
        errors.push('Invalid phone number format (should be 10-11 digits)');
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    // Validate form data
    const validation = validateJobApplicationData(formData);
    if (!validation.isValid) {
      setSubmitError(validation.errors.join(', '));
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      let resumeFileData: {
        resumeFileName?: string;
        resumeFilePath?: string;
        resumeFileSize?: number;
        resumeMimeType?: string;
      } = {};
      
      // Upload resume to Digital Ocean Spaces if file is selected
      if (selectedFile) {
        console.log('Uploading resume to Digital Ocean Spaces...');
        const uploadResult = await digitalOceanSpacesService.uploadResume(selectedFile, formData.name);
        
        if (!uploadResult.success) {
          setSubmitError(`Resume upload failed: ${uploadResult.error}`);
          return;
        }
        
        resumeFileData = {
          resumeFileName: uploadResult.fileName || undefined,
          resumeFilePath: uploadResult.filePath || undefined,
          resumeFileSize: uploadResult.fileSize || undefined,
          resumeMimeType: uploadResult.mimeType || undefined
        };
        
        console.log('Resume uploaded successfully:', resumeFileData);
      }

      // Prepare job application data with resume file metadata
      const applicationData = {
        ...formData,
        ...resumeFileData
      };

      // Submit job application to the API
      const response = await apiClient.jobApplications.createJobApplication(applicationData);
      
      if (response.isSuccessful) {
        // Store submitted data for modal display
        const dataForModal = {
          ...formData,
          resumeFile: selectedFile ? selectedFile.name : 'Not provided',
          resumeUploaded: !!resumeFileData.resumeFileName
        };
        setSubmittedData(dataForModal as any);
        setShowSuccessModal(true);
        // Form will be reset when modal is closed
      } else {
        setSubmitError(response.errors[0].message || 'Failed to submit job application');
      }
    } catch (error: any) {
      console.error('Error submitting job application:', error);
      if (error?.message) {
        setSubmitError(error.message);
      } else {
        setSubmitError('An unexpected error occurred. Please try again later.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section ref={sectionRef} className={`${styles.careersSection} trowel-background`}>
      <img
        src={joinTeamIcon}
        alt="Join Our Team Icon"
        className={`${styles.icon} ${visibleElements.includes(0) ? styles.rotateIn : styles.animationHidden}`}
      />

      <h1 className={`${styles.title} ${visibleElements.includes(1) ? styles.fadeInDown : styles.animationHidden}`}>
        Join Our Team!
      </h1>
      <p className={`${styles.intro} ${visibleElements.includes(2) ? styles.fadeInUp : styles.animationHidden}`}>
        We're always looking for talented and passionate tilers to join LB Tiling.
      </p>

      {/* <h3 className={styles.subheading}>Current Openings:</h3>
      <ul className={styles.openingsList}>
        <li>Experienced Tiler</li>
        <li>Apprentice Tiler</li>
      </ul> */}

      <p className={`${styles.instruction} ${visibleElements.includes(3) ? styles.fadeInUp : styles.animationHidden}`}>
        To apply, please fill out the form below and upload your resume.
      </p>
      {/* <p className={styles.contactNote}><em>* Either email or phone number is required</em></p> */}

      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={`${styles.formGroup} ${visibleElements.includes(4) ? styles.slideInLeft : styles.animationHidden}`}>
          <label htmlFor="career-name">Full Name:</label>
          <input
            type="text"
            id="career-name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
          />
        </div>

        <div key={'career-email'} className={`${styles.formGroup} ${visibleElements.includes(5) ? styles.slideInRight : styles.animationHidden}`}>
          <label htmlFor="career-email">Email:</label>
          <input
            type="email"
            id="career-email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="your.email@example.com"
          />
        </div>

        <div key={'career-phone'} className={`${styles.formGroup} ${visibleElements.includes(6) ? styles.slideInLeft : styles.animationHidden}`}>
          <label htmlFor="career-phone">Phone:</label>
          <input
            type="tel"
            id="career-phone"
            name="phone"
            value={formData.phone || ''}
            onChange={handleInputChange}
            placeholder="(250) 555-1234"
          />
        </div>

        <div className={`${styles.formGroup} ${visibleElements.includes(7) ? styles.slideInRight : styles.animationHidden}`}>
          <label htmlFor="positionAppliedFor">Position Applied For:</label>
          <input
            type="text"
            id="positionAppliedFor"
            name="positionAppliedFor"
            value={formData.positionAppliedFor}
            onChange={handleInputChange}
            placeholder="e.g., Experienced Tiler, Apprentice Tiler"
            required
          />
        </div>

        <div className={`${styles.formGroup} ${visibleElements.includes(8) ? styles.slideInLeft : styles.animationHidden}`}>
          <label htmlFor="resume">Resume:</label>
          <CustomFileInput
            id="resume"
            name="resume"
            accept=".pdf,.doc,.docx"
            onChange={handleFileChange}
            value={selectedFile}
          />
        </div>

        {submitError && (
          <div className={styles.errorMessage}>
            {submitError}
          </div>
        )}

        <div className={visibleElements.includes(9) ? styles.fadeInUp : styles.animationHidden}>
          <Button
            type="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            Submit Application
          </Button>
        </div>
      </form>

      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          // Clear form when modal is closed
          setFormData({ name: '', email: '', phone: '', positionAppliedFor: '' });
          setSelectedFile(null);
          // Reset file input
          const fileInput = document.getElementById('resume') as HTMLInputElement;
          if (fileInput) fileInput.value = '';
        }}
        title="Job Application Submitted!"
        message="Thank you for your interest in joining our team! We have received your application and will review it carefully. We'll get back to you soon."
        submittedData={submittedData || {}}
      />
    </section>
  );
};

export default Careers;